/**
 * Um curso — módulos e aulas em ordem, com o estado de cada aula.
 *
 * A ordem da tela é a ordem em que a pessoa deve assistir, e ela vem PRONTA do servidor
 * (`sort_order` de módulo e de aula). Reordenar aqui faria a trilha mudar conforme quem
 * abriu, e "continuar de onde parei" deixaria de fazer sentido.
 *
 * Três decisões:
 *
 *  - **aula sem vídeo não vira aula de segunda classe**: ela aparece na mesma lista, na
 *    mesma posição, com um selo dizendo o que está faltando. É o estado normal do produto
 *    hoje, não um defeito a esconder (invariante 12);
 *  - **o estado sai de `completedAt`, nunca da posição do player** — quem arrastou até o
 *    fim e não clicou fica em "Começou" (ver `lessonState`);
 *  - **um cartão por módulo, não card dentro de card**: as aulas são linhas separadas por
 *    divisor, que é o que o DESIGN_SYSTEM manda (seção 12, "excesso de cards").
 */
import { Link, useParams } from 'react-router';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
  SkeletonRegion,
  SkeletonText,
  cn,
} from '@mgdlead/ui';
import { ArrowLeft, Check, ChevronRight, VideoOff } from 'lucide-react';

import { PageHeader } from '@mgdlead/ui';
import { ProgressBar } from '../../../features/training/ProgressBar';
import {
  LESSON_STATE_LABEL,
  SEM_VIDEO,
  formatDuration,
  lessonPath,
  lessonState,
  progressLabel,
  progressPercent,
  semVideoHint,
} from '../../../features/training/training-ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { useTrainingCourse } from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';

export function CoursePage() {
  const { workspace } = useWorkspace();
  const { courseSlug = '' } = useParams<{ courseSlug: string }>();

  const cursoQuery = useTrainingCourse(courseSlug);
  const curso = cursoQuery.data;

  useDocumentTitle(curso?.title ?? 'Treinamentos');

  const voltar = (
    <Link
      to={`/w/${workspace.slug}/treinamentos`}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft aria-hidden="true" className="size-4" />
      Todos os cursos
    </Link>
  );

  if (cursoQuery.isError) {
    return (
      <>
        <div className="mb-3">{voltar}</div>
        <Card padded={false}>
          <ErrorState
            title="Não foi possível abrir o curso"
            description={humanizeError(cursoQuery.error)}
            onRetry={() => void cursoQuery.refetch()}
          />
        </Card>
      </>
    );
  }

  if (curso === undefined) {
    return (
      <SkeletonRegion label="Carregando o curso" className="flex flex-col gap-3">
        <Skeleton className="h-5 w-2/5" />
        <SkeletonText lines={2} />
        <Card>
          <Skeleton className="mb-3 h-4 w-1/3" />
          <SkeletonText lines={3} />
        </Card>
      </SkeletonRegion>
    );
  }

  const aviso = semVideoHint(curso.lessonsWithoutVideo);

  return (
    <>
      <div className="mb-3">{voltar}</div>

      <PageHeader title={curso.title} description={curso.description ?? undefined} />

      {curso.totalLessons === 0 ? (
        <Card padded={false}>
          <EmptyState
            kind="no-data"
            title="Este curso ainda está sendo montado"
            description="Nenhuma aula publicada por enquanto. Quando as aulas entrarem, elas aparecem aqui na ordem em que devem ser assistidas."
          />
        </Card>
      ) : (
        <>
          <Card className="mb-4">
            <ProgressBar
              completed={curso.completedLessons}
              total={curso.totalLessons}
              percent={progressPercent(curso.completedLessons, curso.totalLessons)}
              label={progressLabel(curso.completedLessons, curso.totalLessons)}
            />
            {aviso ? (
              <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
                <VideoOff aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span>{aviso}</span>
              </p>
            ) : null}
          </Card>

          <div className="flex flex-col gap-4">
            {curso.modules.map((modulo, indice) => (
              <Card key={modulo.id} padded={false}>
                <div className="border-b border-border p-3 md:p-4">
                  <p className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                    Módulo {indice + 1}
                  </p>
                  <h2 className="mt-0.5 text-md font-medium">{modulo.title}</h2>
                  {modulo.description ? (
                    <p className="mt-1 max-w-prose text-sm text-muted-foreground">
                      {modulo.description}
                    </p>
                  ) : null}
                </div>

                <ul>
                  {modulo.lessons.map((aula) => {
                    const estado = lessonState(aula);
                    const duracao = formatDuration(aula.durationSeconds);
                    return (
                      <li key={aula.id} className="border-b border-border last:border-b-0">
                        <Link
                          to={lessonPath(workspace.slug, curso.slug, aula.slug)}
                          className="flex min-h-11 items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none md:px-4"
                        >
                          {/*
                            O estado tem DOIS canais: o ícone/traço e o texto do selo.
                            Cor sozinha é anti-padrão declarado (DESIGN_SYSTEM seção 12).
                          */}
                          <span
                            aria-hidden="true"
                            className={cn(
                              'flex size-5 shrink-0 items-center justify-center rounded-sm border',
                              estado === 'concluida'
                                ? 'border-success bg-success/15 text-success'
                                : 'border-border text-muted-foreground',
                            )}
                          >
                            {estado === 'concluida' ? <Check className="size-3" /> : null}
                          </span>

                          {/*
                            O selo "Sem vídeo ainda" vai ABAIXO do título, junto do estado
                            — não ao lado. Ao lado, ele comia a largura num 390px e o
                            título virava "Como o painel enxerga…", que é justamente o que
                            a pessoa precisa ler para escolher a aula.
                          */}
                          <span className="min-w-0 flex flex-1 flex-col gap-1">
                            <span className="text-sm font-medium">{aula.title}</span>
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                              <span>{LESSON_STATE_LABEL[estado]}</span>
                              {duracao ? <span className="tabular-nums">{duracao}</span> : null}
                              {!aula.hasVideo ? (
                                <Badge tone="neutral" icon={<VideoOff className="size-3" />}>
                                  {SEM_VIDEO.badge}
                                </Badge>
                              ) : null}
                            </span>
                          </span>

                          <ChevronRight
                            aria-hidden="true"
                            className="size-4 shrink-0 text-muted-foreground"
                          />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            ))}
          </div>
        </>
      )}
    </>
  );
}
